import React from "react";
import { createPortal } from "react-dom";
import { Edit3 } from "lucide-react";
import { Button } from "@repo/ui";
import { Input } from "@repo/ui";
import { Label } from "@repo/ui";
import { VariableDef } from "../variable-input";

export interface VariableEditPopupProps {
  isOpen: boolean;
  onClose: () => void;
  variable: VariableDef | null;
  onVariableUpdate: (oldVariableName: string, newVariableName: string) => void;
  position: { x: number; y: number } | null;
  container?: Element | DocumentFragment | null;
  /** Set to false when inside a Radix Sheet/Dialog to avoid inert issues */
  usePortal?: boolean;
}

export const VariableEditPopup: React.FC<VariableEditPopupProps> = ({
  isOpen,
  onClose,
  variable,
  onVariableUpdate,
  position,
  container,
  usePortal = true,
}) => {
  const [variableValue, setVariableValue] = React.useState<string>("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (variable) {
      setVariableValue(variable.name);
    }
  }, [variable]);

  // Focus input after popup renders
  React.useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const handleSave = React.useCallback(() => {
    if (variable && variableValue.trim() && variableValue.trim() !== variable.name) {
      onVariableUpdate(variable.name, variableValue.trim());
    }
    onClose();
  }, [variable, variableValue, onVariableUpdate, onClose]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        event.stopPropagation();
        onClose();
      } else if (event.key === "Enter" && isOpen) {
        event.stopPropagation();
        handleSave();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown, true);
      return () => document.removeEventListener("keydown", handleKeyDown, true);
    }
  }, [isOpen, onClose, handleSave]);

  if (!isOpen || !variable || !position) {
    return null;
  }

  const popupContent = (
    <>
      {/* Overlay to catch clicks outside */}
      <div
        data-variable-edit-popup="true"
        className="fixed inset-0 z-[999998]"
        style={{ pointerEvents: "auto" }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />
      <div
        className="variable-edit-popup fixed z-[999999] w-80 rounded-lg border bg-white p-3 shadow-lg"
        data-variable-edit-popup="true"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          pointerEvents: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2">
          <Edit3 className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium">Edit Variable</span>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Variable Path</Label>
            <Input
              ref={inputRef}
              value={variableValue}
              onChange={(e) => setVariableValue(e.target.value)}
              placeholder="e.g. customer.id"
              className="font-mono text-sm"
            />
            <div className="mt-1 text-xs text-gray-500">Current: ${"{" + variable.name + "}"}</div>
          </div>

          <div className="flex justify-end gap-2 border-t pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!variableValue.trim() || variableValue.trim() === variable.name}
            >
              Update
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  // An explicit container (e.g. the sheet's own node) is used when the popup would
  // otherwise be trapped in a transformed ancestor (a nested Radix Popover) that
  // breaks its fixed positioning. Otherwise: portal to body for standalone use, or
  // render inline when inside a Radix Sheet/Dialog (a body portal there is inert).
  if (container) {
    return createPortal(popupContent, container);
  }
  if (usePortal) {
    return createPortal(popupContent, document.body);
  }
  return popupContent;
};

export default VariableEditPopup;
